import { useLayoutContext } from "@router/layouts/MainLayout";
import { PageLoading } from "@shared/components/PageLoading";
import { ProfileCardView } from "@shared/components/ProfileCardView";
import { useUser } from "@shared/queries/useUser";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

/**
 * Match profile page: shows a matched user's profile in card view.
 * Route: /match/:userId. Entry from Matches (e.g. avatar tap or profile action).
 */
export function MatchProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading, error, isError } = useUser(userId);
  const { setHeader } = useLayoutContext();
  const navigate = useNavigate();

  useEffect(() => {
    setHeader({
      variant: "back",
      // no title
      onBack: () => navigate(-1),
    });
    return () => setHeader(null); // clean up header when component unmounts
  }, [setHeader, navigate]);

  if (!userId) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Invalid link</h2>
          <p className="text-gray-600">No user specified.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <PageLoading message="Loading profile..." />;
  }

  if (isError || error) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Could not load profile</h2>
          <p className="text-gray-600">{error instanceof Error ? error.message : "Something went wrong."}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50 p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Profile not found</h2>
          <p className="text-gray-600">This user may no longer be available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-[60vh] w-full" data-testid="match-profile-page">
      <ProfileCardView user={user} />
    </div>
  );
}
